import React, { useEffect } from "react";

import { useMutation } from "@apollo/client";
import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { makeStyles, withStyles } from "tss-react/mui";

import { graphql } from "@/gql";
import useDialogState from "@/hooks/useDialogState";
import { useFragment } from "@/hooks/useFragment";
import useGlobalSnackbar from "@/hooks/useGlobalSnackbar";
import { colors } from "@/theme/overrides/colors";
import { format } from "@/utils/datetime";
import { displayError } from "@/utils/errors";
import { apply } from "@/utils/functions";
import { InferValues, muiError } from "@/utils/hookForm";

import FormConfirmationDialog from "../../components/FormConfirmationDialog";

interface Props {
  open: boolean;
  onClose: () => void;
  id: string;
}
const useStyles = makeStyles()((theme) => ({
  modalHeader: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    margin: theme.spacing(4, 3, 0, 3),
  },
  modalHeaderText: {
    fontSize: "28px",
    fontWeight: "bold",
  },
  titleText: {
    fontSize: "14px",
    fontWeight: 700,
    color: colors.dark,
    marginBottom: "20px",
  },
  fields: {
    width: "100%",
  },
  actions: {
    justifyContent: "center",
    padding: theme.spacing(2, 5, 9),
  },
}));

const FieldLabel = withStyles(Typography, {
  root: {
    color: colors.gray,
    fontSize: "14px",
    marginBottom: "12px",
    "& label": {
      color: "#D13F3F",
    },
  },
});

const sendReminderDialogDefaultDescriptionFragment = graphql(`
  fragment ReminderDefaultDescription on SentForm {
    id
    client {
      id
      profile {
        id
        fullName
      }
    }
    form {
      id
      title
    }
    createdAt
  }
`);

const sendReminderMutation = graphql(`
  mutation SendReminder($input: SendFormResponseReminderInput!) {
    sendFormResponseReminder(input: $input) {
      ok
    }
  }
`);

const SendReminderDialog: React.FC<Props> = ({ open, onClose, id }) => {
  const { classes } = useStyles();
  const snackbar = useGlobalSnackbar();
  const successDialog = useDialogState();

  const [data] = useFragment(
    sendReminderDialogDefaultDescriptionFragment,
    `SentForm:${id}`
  );
  const [sendReminder] = useMutation(sendReminderMutation);

  const defaultDescription = `Hi ${
    data?.client?.profile?.fullName
  },\n\nI have sent the form ${data?.form.title} last ${apply(
    data?.createdAt,
    (datetime) => format({ datetime }, "PP")
  )}. If you can kindly fill this up for me.\n\nThank you.`;
  const { control, handleSubmit, reset } = useForm({
    defaultValues: {
      description: defaultDescription,
    },
  });

  async function onSubmit(values: InferValues<typeof control>) {
    onClose();
    successDialog.show();
    try {
      await sendReminder({
        variables: {
          input: { sentFormId: id, description: values.description },
        },
      });
      successDialog.show();
    } catch (e) {
      snackbar.open(displayError(e, "Unable to send reminder."));
    }
  }

  useEffect(() => {
    if (open) reset({ description: defaultDescription });
  }, [open]);

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Box className={classes.modalHeader}>
            <Typography className={classes.modalHeaderText}>
              Send Reminder
            </Typography>
            <IconButton onClick={onClose} size="large">
              <CloseIcon />
            </IconButton>
          </Box>
          <DialogContent>
            <Box>
              <FieldLabel>Description</FieldLabel>
              <Controller
                name="description"
                control={control}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    {...muiError(fieldState)}
                    className={classes.fields}
                    type="string"
                    multiline
                    rows={6}
                    required
                  />
                )}
              />
            </Box>
          </DialogContent>
          <DialogActions className={classes.actions}>
            <Button color="primary" type="submit">
              Send
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <FormConfirmationDialog
        onClose={successDialog.hide}
        open={successDialog.shown}
        status="success"
        message="Reminder has been successfully sent to "
        fullName={data?.client?.profile?.fullName ?? ""}
      />
    </>
  );
};

export default SendReminderDialog;
